/**
 * Velkomsten til den nye nettbutikken (lanseringen 5. oktober 2026). Markup og innstillinger
 * står i snippets/skm-velkomst.liquid.
 *
 * Vises én gang per nettleser, og aldri over samtykkebanneret:
 * - Lagringen er én nøkkel i localStorage, skrevet når kunden lukker brevet eller følger en
 *   lenke i det. Den husker bare at brevet er lest, så den hører til selve funksjonen kunden
 *   har brukt (som bannerets eget «valgt»-minne), og sendes ingen steder. Lar lagringen seg
 *   ikke lese eller skrive, vises brevet ikke i det hele tatt, fordi det ellers ville kommet
 *   på hver side.
 * - Samtykkebanneret skal være besvart først, både temaets eget (`#SkmSamtykke` med
 *   preferansevinduet `#SkmSamtykkeValg`, assets/skm-samtykkebanner.js, fra 2026-10-05) og
 *   Shopifys (`#shopify-pc__banner`), som temaets banner viker for. Temaets banner bestemmer seg
 *   tidligst SHOPIFY_VENTETID_MS (1500 ms) etter lasting, så brevet bestemmer seg først etter
 *   ROLIG_START_MS. Målt i butikken 2026-09-13
 *   (docs/cookie-grunnlag/maaling-2026-09-13/tilstand/03 og 04): `shouldShowBanner()` svarer
 *   true også etter et valg, så det kan ikke brukes alene. Før valget svarer
 *   `shouldShowGDPRBanner()` true og `currentVisitorConsent()` har tomme felt; etter valget er
 *   det false og «yes»/«no». I tillegg leses bannerne selv i DOM-en, fordi det er det kunden
 *   faktisk ser. Bannerne og valgene røres aldri herfra.
 * - Dukker banneret opp mens brevet står åpent, lukkes brevet uten å huske at det er lest,
 *   så banneret aldri blir sperret av den modale dialogen.
 *
 * `?velkomst=vis` i adressen åpner brevet uansett lagring og nettleserens datosjekk, og husker
 * ingenting. Det overstyrer ikke Liquid-sjekken av siste dag eller av/på i Theme Editor; etter
 * siste dag rendres brevet ikke. Det er for forhåndsvisning og QA.
 */

const NOEKKEL = 'skm-velkomst-ny-nettbutikk-2026';
const SHOPIFY_BANNER_ID = 'shopify-pc__banner';
const EGET_BANNER_ID = 'SkmSamtykke';
const EGET_VINDU_ID = 'SkmSamtykkeValg';
const HENDELSE = 'visitorConsentCollected';
const API_VENTETID_MS = 3000;
const ROLIG_START_MS = 2000;

/** Dagens dato i norsk tid som ÅÅÅÅ-MM-DD. */
function idagOslo() {
  try {
    return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Oslo' }).format(new Date());
  } catch (error) {
    return new Date().toISOString().slice(0, 10);
  }
}

/** @returns {boolean | null} true = lest, false = ikke lest, null = lagring utilgjengelig */
function erLest() {
  try {
    return window.localStorage.getItem(NOEKKEL) === 'lest';
  } catch (error) {
    return null;
  }
}

function merkLest() {
  try {
    window.localStorage.setItem(NOEKKEL, 'lest');
  } catch (error) {
    // Ingenting å gjøre; uten lagring vises brevet uansett ikke igjen (se erLest).
  }
}

/** @param {HTMLElement | null} banner */
function elementSynlig(banner) {
  if (!banner || banner.hidden) return false;
  const stil = window.getComputedStyle(banner);
  if (stil.display === 'none' || stil.visibility === 'hidden' || Number(stil.opacity) === 0) return false;
  const rekt = banner.getBoundingClientRect();
  return rekt.width > 0 && rekt.height > 0;
}

/** Om et samtykkebanner eller preferansevinduet står framme nå. */
function bannerSynlig() {
  const vindu = /** @type {HTMLDialogElement | null} */ (document.getElementById(EGET_VINDU_ID));
  if (vindu && vindu.open) return true;
  return elementSynlig(document.getElementById(EGET_BANNER_ID)) || elementSynlig(document.getElementById(SHOPIFY_BANNER_ID));
}

/** @param {number} ms */
const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Customer Privacy API, eller null ved feil eller etter API_VENTETID_MS. */
function lastPersonvernApi() {
  return new Promise((resolve) => {
    const shopify = /** @type {any} */ (window).Shopify;
    if (!shopify || typeof shopify.loadFeatures !== 'function') {
      resolve(null);
      return;
    }
    const klokke = setTimeout(() => resolve(null), API_VENTETID_MS);
    try {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (/** @type {any} */ feil) => {
        clearTimeout(klokke);
        resolve(feil ? null : /** @type {any} */ (window).Shopify?.customerPrivacy || null);
      });
    } catch (error) {
      clearTimeout(klokke);
      resolve(null);
    }
  });
}

/** @param {any} api */
function venterPaaValg(api) {
  if (!api) return false;
  try {
    if (typeof api.shouldShowGDPRBanner === 'function' && api.shouldShowGDPRBanner() === true) return true;
    if (typeof api.shouldShowBanner === 'function' && api.shouldShowBanner() === true) {
      const samtykke = typeof api.currentVisitorConsent === 'function' ? api.currentVisitorConsent() : null;
      if (!samtykke) return false;
      return ['marketing', 'analytics', 'preferences'].some((felt) => !samtykke[felt]);
    }
  } catch (error) {
    return false;
  }
  return false;
}

/**
 * Venter på at kunden har svart: hendelsen fra Shopify, eller at et banner som sto framme er
 * borte igjen (kunden har trykket, selv om lagringen av en eller annen grunn ikke meldte fra).
 */
function ventPaaValg() {
  return new Promise((resolve) => {
    let sett = false;
    const ferdig = () => {
      clearInterval(sjekk);
      document.removeEventListener(HENDELSE, ferdig);
      resolve(undefined);
    };
    const sjekk = setInterval(() => {
      if (bannerSynlig()) sett = true;
      else if (sett) ferdig();
    }, 400);
    document.addEventListener(HENDELSE, ferdig);
  });
}

function ventTilBannerBorte() {
  return new Promise((resolve) => {
    if (!bannerSynlig()) {
      resolve(undefined);
      return;
    }
    const sjekk = setInterval(() => {
      if (bannerSynlig()) return;
      clearInterval(sjekk);
      resolve(undefined);
    }, 400);
  });
}

class SkmVelkomst extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLDialogElement | null} */
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

    this.tvunget = new URLSearchParams(window.location.search).get('velkomst') === 'vis';
    this.forrigeFokus = /** @type {HTMLElement | null} */ (null);

    this.dialog.addEventListener('close', () => this.etterLukking());
    this.dialog.addEventListener('click', (event) => {
      // Klikk utenfor brevet treffer selve dialogen (den dekker hele vinduet).
      if (event.target === this.dialog) this.lukk();
    });
    this.querySelectorAll('[data-velkomst-lukk]').forEach((knapp) => {
      knapp.addEventListener('click', () => this.lukk());
    });
    this.dialog.querySelectorAll('a[href]').forEach((lenke) => {
      lenke.addEventListener('click', () => {
        if (!this.tvunget) merkLest();
      });
    });

    if (this.tvunget) {
      this.aapneNaarRolig();
      return;
    }
    if (this.dataset.designmodus === 'true') return;
    const slutt = this.dataset.slutt || '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(slutt) || idagOslo() > slutt) return;
    if (erLest() !== false) return;
    this.aapneNaarRolig();
  }

  async aapneNaarRolig() {
    if (document.readyState !== 'complete') {
      await new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));
    }
    await pause(ROLIG_START_MS);

    const api = await lastPersonvernApi();
    if (venterPaaValg(api)) {
      await ventPaaValg();
    } else if (!api) {
      // Uten API: gi banneret tid til å komme, og les det som står på skjermen.
      await pause(1500);
    }
    await pause(400);
    await ventTilBannerBorte();
    await pause(400);
    // Har kunden rukket å lukke eller lese brevet i en annen fane imens, står det over.
    if (!this.tvunget && erLest() !== false) return;
    this.aapne();
  }

  aapne() {
    const dialog = /** @type {HTMLDialogElement} */ (this.dialog);
    if (dialog.open) return;
    this.forrigeFokus = /** @type {HTMLElement | null} */ (document.activeElement);
    document.documentElement.classList.add('skm-velkomst-aapen');
    dialog.showModal();
    dialog.scrollTop = 0;
    this.vakt = new MutationObserver(() => {
      if (bannerSynlig()) this.lukk({ husk: false });
    });
    this.vakt.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'open'] });
  }

  /** @param {{ husk?: boolean }} [valg] */
  lukk({ husk = true } = {}) {
    this.husk = husk;
    if (this.dialog?.open) this.dialog.close();
  }

  etterLukking() {
    this.vakt?.disconnect();
    document.documentElement.classList.remove('skm-velkomst-aapen');
    if (this.husk !== false && !this.tvunget) merkLest();
    this.husk = true;
    const fokus = this.forrigeFokus;
    if (fokus && typeof fokus.focus === 'function' && document.contains(fokus)) fokus.focus();
  }
}

if (!customElements.get('skm-velkomst')) customElements.define('skm-velkomst', SkmVelkomst);
